import { useMemo, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { outcomeGroup, type Outcome, type OutcomeGroup, type RowResult } from "./runUpload.js";
import { matchesSearch, referenceTypeLabel } from "./searchResults.js";
import styles from "./BulkExternalRefs.module.css";

export type ResultsFilter = OutcomeGroup | "all";

type ResultsTableProps = {
  results: RowResult[];
  filter: ResultsFilter;
  onFilterChange: (filter: ResultsFilter) => void;
  onExport: () => void;
  onRetryFailed: () => void;
  isBusy: boolean;
  /** Names the value column, so a saved run reads correctly for its own type. */
  referenceType: string;
};

type TableRow = {
  id: string;
  company: string;
  companyUuid: string;
  value: string;
  outcome: Outcome;
  detail: string;
};

const OUTCOME_LABELS: Record<Outcome, string> = {
  created: "Created",
  "already-mapped": "Already mapped",
  "duplicate-in-file": "Already mapped",
  malformed: "Invalid row",
  "not-found": "Not found",
  rejected: "Rejected",
  "writes-disabled": "Writes disabled",
  failed: "Failed",
};

const PILL_CLASSES: Record<OutcomeGroup, string> = {
  created: styles.pillCreated as string,
  skipped: styles.pillSkipped as string,
  failed: styles.pillFailed as string,
};

const FILTERS: ResultsFilter[] = ["all", "created", "skipped", "failed"];

const FILTER_LABELS: Record<ResultsFilter, string> = {
  all: "All",
  created: "Created",
  skipped: "Skipped",
  failed: "Failed",
};

export function ResultsTable({
  results,
  filter,
  onFilterChange,
  onExport,
  onRetryFailed,
  isBusy,
  referenceType,
}: ResultsTableProps) {
  const [search, setSearch] = useState("");
  const counts = useMemo(() => {
    const tally: Record<ResultsFilter, number> = {
      all: results.length,
      created: 0,
      skipped: 0,
      failed: 0,
    };
    for (const result of results) {
      tally[outcomeGroup(result.outcome)] += 1;
    }
    return tally;
  }, [results]);

  const rows = useMemo<TableRow[]>(
    () =>
      results
        .filter(result => filter === "all" || outcomeGroup(result.outcome) === filter)
        .filter(result => matchesSearch(result, search))
        .map(result => ({
          id: `${result.row.lineNumber}-${result.row.companyUuid}-${result.row.referenceValue}`,
          company: result.row.label,
          companyUuid: result.row.companyUuid,
          value: result.row.referenceValue,
          outcome: result.outcome,
          detail: result.detail,
        })),
    [results, filter, search],
  );

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: "Company",
        accessor: "company",
        wrap: true,
        render: (_value, row) => (
          <div>
            <div>{row.company || "—"}</div>
            {row.detail ? <div className={styles.rowNote}>{row.detail}</div> : null}
          </div>
        ),
      },
      {
        header: "Company UUID",
        accessor: "companyUuid",
        render: (_value, row) => <span className={styles.mono}>{row.companyUuid}</span>,
      },
      {
        header: referenceTypeLabel(referenceType),
        accessor: "value",
        render: (_value, row) => <span className={styles.numeric}>{row.value}</span>,
      },
      {
        header: "Outcome",
        accessor: "outcome",
        render: (_value, row) => (
          <span className={`${styles.pill} ${PILL_CLASSES[outcomeGroup(row.outcome)]}`}>
            {OUTCOME_LABELS[row.outcome]}
          </span>
        ),
      },
    ],
    [referenceType],
  );

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search company, UUID or Mondaq ID"
          aria-label="Search results"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <span className={styles.spacer} />
      </div>

      <div className={styles.toolbar}>
        <div className={styles.chips}>
          {FILTERS.map(option => (
            <button
              key={option}
              type="button"
              className={`${styles.chip} ${filter === option ? styles.chipActive : ""}`}
              aria-pressed={filter === option}
              onClick={() => onFilterChange(option)}
            >
              {FILTER_LABELS[option]} {counts[option]}
            </button>
          ))}
        </div>
        <span className={styles.spacer} />
        <Button variant="outlined" onClick={onExport} disabled={results.length === 0}>
          Export CSV
        </Button>
        <Button variant="outlined" onClick={onRetryFailed} disabled={isBusy || counts.failed === 0}>
          Retry failed
        </Button>
      </div>

      <DataTable data={rows} columns={columns} emptyMessage="No rows match." />

      <span className={styles.resultCount}>
        Showing {rows.length} of {results.length}
      </span>
    </div>
  );
}
